import { useState, useCallback } from 'react';
import dataValidationAPI from '../../../../../services/dataValidation.api';

export interface ValidationRule {
  id: number;
  description: string;
  status: string;
  fields?: { name: string }[];
  container?: { id: number; name: string };
  global_tags?: { name: string }[];
  enrichmentDatastoreId?: number;
  enrichmentDatastoreName?: string;
  connectorName?: string;
}

export interface ValidationRuleDetailsResponse {
  data: {
    anomalyUUID: string;
    anomalyID?: number;
    lastRun: string;
    anomalousRecords?: number;
    failedSourceRecords: unknown[];
    failedColumns: string[];
    tableRecordCount: number;
    failedRowCountData: {
      date: number;
      failedRowCount: number;
      id: string;
    }[];
  };
}

export const useDataValidation = () => {
  // State for all rules
  const [rules, setRules] = useState<ValidationRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for rule details
  const [ruleDetails, setRuleDetails] =
    useState<ValidationRuleDetailsResponse | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // Fetch validation rules
  const fetchValidationRules = useCallback(
    async (tables: string[], connectorNames: string[]) => {
      setLoading(true);
      setError(null);

      try {
        const data = await dataValidationAPI.getValidationRules(
          tables,
          connectorNames
        );
        setRules(data);
      } catch (err: any) {
        console.error('Error fetching validation rules:', err);
        setError(err.message || 'Failed to fetch validation rules');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Fetch rule details
  const fetchRuleDetails = useCallback(
    async (ruleId: number, tableId: number, datastoreId?: number) => {
      setDetailsLoading(true);
      setDetailsError(null);

      try {
        const data = await dataValidationAPI.getValidationRuleDetails(
          ruleId,
          tableId,
          datastoreId
        );
        setRuleDetails(data);
      } catch (err: any) {
        console.error('Error fetching rule details:', err);
        setDetailsError(err.message || 'Failed to fetch rule details');
      } finally {
        setDetailsLoading(false);
      }
    },
    []
  );

  // Run a scheduled scan
  const runScan = useCallback(async () => {
    try {
      return await dataValidationAPI.runScheduledScan();
    } catch (err: any) {
      console.error('Error running scan:', err);
      throw err;
    }
  }, []);

  // Get scan status
  const getScanStatus = useCallback(async (scheduleId: number) => {
    try {
      return await dataValidationAPI.getScanStatus(scheduleId);
    } catch (err: any) {
      console.error('Error getting scan status:', err);
      throw err;
    }
  }, []);

  return {
    // Rules list
    rules,
    loading,
    error,
    fetchValidationRules,

    // Rule details
    ruleDetails,
    detailsLoading,
    detailsError,
    fetchRuleDetails,

    // Operations
    runScan,
    getScanStatus,
  };
};
